import { WSDataItemProps, WSGrid } from "@wingspanhq/fe-component-library";
import React from "react";
import { FormType, PayerLinkRequestStatus } from "@wingspanhq/wingspan-api-sdk";
import { queriesForms, queriesPayees } from "@query";
import { DataItem } from "../../../shared/components/DataItem";
import { selectorCountryName } from "../../../shared/selectors/selectorCountryName";
import {
  getTaxIdLabel,
  getTaxIdVerificationAlert
} from "../../../shared/utils/taxId";
import { useQueryContractorComplianceEntity } from "../hooks/useContractorTaxCompliance";
import { selectorContractorTaxDisplay } from "../selectors/selectorContractorTaxDisplay";
import { useXWingspanPayerId } from "../routes/RoutePayeeDetails/useXWingspanPayerId";
import { useEditEmail } from "./EditEmail/useEditEmail";

type Props = {
  payeeId: string;
  editable?: boolean;
};

/**
 * v3 contractor W-9 / tax display. Self-fetches the payee + its tax identity
 * (dual-source via `selectorContractorTaxDisplay`: payer-supplied
 * `ComplianceEntity`, else the payee-owned Auto-W-9 `Form`). Read-only INTERIM —
 * built against the SDK, not runtime-verified (tax PII); payee-owned verdict is
 * coarse (Effective/Expired). See V3_MIGRATION_TRACKER.md (payees).
 */
export const ContractorInformation: React.FC<Props> = props => {
  const editEmail = useEditEmail();
  // v3-migration (contractor-details, user-confirmed 2026-07-22): the v1
  // experiments flags that gated the "1099 filing paused" TIN alert
  // (`is1099FilingPaused`) and the Edit-email action (`editContractorEmail`)
  // are dropped to keep this screen off v1 (experiments has no v3 twin). The
  // TIN alert now never treats verification as paused, and Edit-email is shown
  // whenever the payee is editable. See V3_MIGRATION_TRACKER.md.
  const xWingspanAccount = useXWingspanPayerId();

  const queryPayee = queriesPayees.getPayee.useQuery({
    payeeId: props.payeeId,
    xWingspanAccount
  });
  const payee = queryPayee.data;

  const queryComplianceEntity = useQueryContractorComplianceEntity(
    props.payeeId
  );

  const queryForm = queriesForms.listForms.useQuery(
    {
      filter: {
        type: { eq: FormType.W9 },
        recipientAccountId: { eq: payee?.payeeAccountId ?? "" },
        isLatest: { eq: true }
      }
    },
    {
      enabled: !!payee?.payeeAccountId,
      select: list => list.data?.[0]
    }
  );

  if (!payee) {
    return null;
  }

  const display = selectorContractorTaxDisplay({
    payee,
    complianceEntity: queryComplianceEntity.data,
    form: queryForm.data
  });

  const country = display?.country || "US";
  // The payer may change the invite email only before the contractor signs up
  // (a pending link request) — the v3 equivalent of v1 `canUpdatePayeeEmail`.
  const canUpdatePayeeEmail =
    payee.linkRequestStatus === PayerLinkRequestStatus.PENDING;

  const fields: WSDataItemProps[] = [
    {
      label: "Name",
      value: display?.legalName
    },
    ...(display?.tinType === "Business" && display?.disregardedEntityName
      ? [
          {
            label: "Disregarded entity name",
            value: display.disregardedEntityName
          }
        ]
      : []),
    {
      label: "Country of residence",
      value: selectorCountryName(country)
    },
    {
      label: "Federal tax classification",
      value: display?.taxClassificationLabel
    },
    {
      label: getTaxIdLabel(display?.tinType ?? "Individual", country),
      value: display?.maskedTaxId,
      alert: getTaxIdVerificationAlert({
        country,
        status: display?.verificationStatus,
        isPendingPaused: false
      })
    },
    {
      label: {
        text: "Email",
        action:
          props.editable && canUpdatePayeeEmail
            ? {
                label: "Edit",
                onClick: () => {
                  editEmail.run({
                    email: payee.email ?? "",
                    payeeId: props.payeeId
                  });
                }
              }
            : undefined
      },
      value: payee.email
    },
    {
      label: "Address",
      value: display?.address
    }
  ];

  return (
    <WSGrid gutter="2XL">
      {fields.map((field, index) => (
        <WSGrid.Item key={index} span={{ xs: "12", s: "6" }}>
          <DataItem {...field} />
        </WSGrid.Item>
      ))}
    </WSGrid>
  );
};
